import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import axios from "axios";

/**
 * Public Lease Builder — no login required. Prospect landlord fills the
 * form, downloads a PA-focused PDF, and gets nudged to sign up so we can
 * manage it ongoing.
 */
const BASE = process.env.REACT_APP_BACKEND_URL;

const LEASE_TYPES = [
  { id: "year",           label: "12-Month Lease",        hint: "Standard fixed-term residential" },
  { id: "month_to_month", label: "Month-to-Month",        hint: "Flexible; 30-day notice either side" },
  { id: "roommate",       label: "Roommate Agreement",    hint: "Co-tenants sharing one unit" },
  { id: "sublease",       label: "Sublease",              hint: "Original tenant subletting to another" },
];

export default function PublicLeaseBuilder() {
  const [f, setF] = useState({
    lease_type: "year",
    state: "PA",
    landlord_name: "",
    landlord_address: "",
    landlord_phone: "",
    landlord_email: "",
    tenant_names_raw: "",
    property_address: "",
    unit: "",
    start_date: "",
    end_date: "",
    rent_monthly_dollars: "",
    security_deposit_dollars: "",
    rent_due_day: 1,
    late_fee_after_days: 5,
    late_fee_dollars: 50,
    pet_policy: "No pets are permitted without prior written consent from the Landlord.",
    utilities_note: "Tenant pays: gas, electric, internet. Landlord pays: water/sewer + trash.",
    parking_note: "",
    custom_clauses_raw: "",
    original_tenant_name: "",
    total_rent_dollars: "",
  });
  const [busy, setBusy] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const download = async () => {
    if (!f.landlord_name.trim()) { toast.error("Landlord name is required"); return; }
    if (!f.property_address.trim() || f.property_address.trim().length < 5) { toast.error("Property address required"); return; }
    if (!f.start_date) { toast.error("Start date required"); return; }
    if (f.lease_type !== "month_to_month" && !f.end_date) { toast.error("End date required for fixed-term lease"); return; }
    if (!f.rent_monthly_dollars || Number(f.rent_monthly_dollars) <= 0) { toast.error("Monthly rent required"); return; }

    setBusy(true);
    try {
      const payload = {
        lease_type: f.lease_type,
        state: f.state.toUpperCase(),
        landlord_name: f.landlord_name.trim(),
        landlord_address: f.landlord_address.trim(),
        landlord_phone: f.landlord_phone.trim(),
        landlord_email: f.landlord_email.trim(),
        tenant_names: f.tenant_names_raw.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
        property_address: f.property_address.trim(),
        unit: f.unit.trim(),
        start_date: f.start_date,
        end_date: f.end_date || null,
        rent_monthly_cents: Math.round(Number(f.rent_monthly_dollars) * 100),
        security_deposit_cents: Math.round(Number(f.security_deposit_dollars || 0) * 100),
        rent_due_day: Number(f.rent_due_day || 1),
        late_fee_after_days: Number(f.late_fee_after_days || 5),
        late_fee_cents: Math.round(Number(f.late_fee_dollars || 0) * 100),
        pet_policy: f.pet_policy,
        utilities_note: f.utilities_note,
        parking_note: f.parking_note,
        custom_clauses: f.custom_clauses_raw.split("\n").map(s => s.trim()).filter(Boolean),
        original_tenant_name: f.original_tenant_name,
        total_rent_cents: Math.round(Number(f.total_rent_dollars || 0) * 100),
      };
      const r = await axios.post(`${BASE}/api/public/lease-builder/pdf`, payload,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lease-${f.landlord_name.replace(/\W+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Lease PDF downloaded");
      setDownloaded(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Lease PDF failed";
      toast.error(typeof msg === "string" ? msg : "Lease PDF failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0B1F3A] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/landlord" className="text-[10px] uppercase tracking-widest text-cyan-300 hover:underline font-black">
            ← Back to Landlord Autopilot
          </Link>
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">Free Lease Builder</h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl">
            Fill the form → download a Pennsylvania-focused residential lease as a PDF. No signup,
            no email. Signed leases can be managed inside Field Radar afterwards.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Lease type */}
        <Section title="1. Lease Type">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {LEASE_TYPES.map(t => (
              <button key={t.id} type="button" onClick={() => upd("lease_type", t.id)}
                data-testid={`lease-type-${t.id}`}
                className={`p-3 border-2 rounded text-left transition-colors ${
                  f.lease_type === t.id ? "border-[#0B1F3A] bg-cyan-50" : "border-slate-200 hover:border-cyan-400"
                }`}>
                <div className="font-black text-sm">{t.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{t.hint}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* Parties */}
        <Section title="2. Parties">
          <Grid>
            <F lbl="Landlord name *"><I v={f.landlord_name} on={v => upd("landlord_name", v)} tid="lb-landlord"/></F>
            <F lbl="Landlord phone"><I v={f.landlord_phone} on={v => upd("landlord_phone", v)} tid="lb-phone"/></F>
            <F lbl="Landlord email"><I v={f.landlord_email} on={v => upd("landlord_email", v)} tid="lb-email"/></F>
            <F lbl="Landlord mailing address"><I v={f.landlord_address} on={v => upd("landlord_address", v)} tid="lb-mail"/></F>
          </Grid>
          <F lbl="Tenant name(s) — one per line or comma-separated *">
            <textarea rows={2} value={f.tenant_names_raw}
              onChange={e => upd("tenant_names_raw", e.target.value)}
              placeholder="e.g., John Smith&#10;Jane Doe"
              data-testid="lb-tenants"
              className={txtCls}/>
          </F>
          {f.lease_type === "sublease" && (
            <F lbl="Original tenant (Sublessor) name">
              <I v={f.original_tenant_name} on={v => upd("original_tenant_name", v)} tid="lb-original"/>
            </F>
          )}
        </Section>

        {/* Premises */}
        <Section title="3. Premises">
          <Grid>
            <F lbl="Property address *"><I v={f.property_address} on={v => upd("property_address", v)} tid="lb-addr"/></F>
            <F lbl="Unit / Apt (optional)"><I v={f.unit} on={v => upd("unit", v)} tid="lb-unit"/></F>
          </Grid>
        </Section>

        {/* Term */}
        <Section title="4. Term">
          <Grid>
            <F lbl="Start date *"><I type="date" v={f.start_date} on={v => upd("start_date", v)} tid="lb-start"/></F>
            {f.lease_type !== "month_to_month" && (
              <F lbl="End date *"><I type="date" v={f.end_date} on={v => upd("end_date", v)} tid="lb-end"/></F>
            )}
          </Grid>
        </Section>

        {/* Rent */}
        <Section title="5. Rent & Deposit">
          <Grid>
            {f.lease_type === "roommate" && (
              <F lbl="Total unit rent (full monthly)">
                <I type="number" v={f.total_rent_dollars} on={v => upd("total_rent_dollars", v)} tid="lb-total-rent"/>
              </F>
            )}
            <F lbl={f.lease_type === "roommate" ? "This roommate's share $/mo *" : "Monthly rent $ *"}>
              <I type="number" v={f.rent_monthly_dollars} on={v => upd("rent_monthly_dollars", v)} tid="lb-rent"/>
            </F>
            <F lbl="Security deposit $ (PA cap: 2× rent year 1, 1× rent after)">
              <I type="number" v={f.security_deposit_dollars} on={v => upd("security_deposit_dollars", v)} tid="lb-deposit"/>
            </F>
            <F lbl="Rent due day of month">
              <I type="number" v={f.rent_due_day} on={v => upd("rent_due_day", v)} tid="lb-due"/>
            </F>
            <F lbl="Late fee $">
              <I type="number" v={f.late_fee_dollars} on={v => upd("late_fee_dollars", v)} tid="lb-lf"/>
            </F>
            <F lbl="Late fee after N days">
              <I type="number" v={f.late_fee_after_days} on={v => upd("late_fee_after_days", v)} tid="lb-lfd"/>
            </F>
          </Grid>
        </Section>

        {/* Utilities + Pet + Parking */}
        <Section title="6. Utilities, Pets & Parking">
          <F lbl="Utilities note">
            <textarea rows={2} value={f.utilities_note} onChange={e => upd("utilities_note", e.target.value)}
              data-testid="lb-utils" className={txtCls}/>
          </F>
          <F lbl="Pet policy">
            <textarea rows={2} value={f.pet_policy} onChange={e => upd("pet_policy", e.target.value)}
              data-testid="lb-pets" className={txtCls}/>
          </F>
          <F lbl="Parking note (optional)">
            <I v={f.parking_note} on={v => upd("parking_note", v)} tid="lb-parking"/>
          </F>
        </Section>

        {/* Custom clauses */}
        <Section title="7. Custom Clauses (optional)">
          <F lbl="One clause per line — added as 'Additional Terms'">
            <textarea rows={4} value={f.custom_clauses_raw} onChange={e => upd("custom_clauses_raw", e.target.value)}
              placeholder={'e.g., "No smoking inside the premises."'}
              data-testid="lb-custom" className={txtCls}/>
          </F>
        </Section>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-6">
          <p className="text-xs text-slate-500 max-w-md">
            This is a template. Not legal advice. PA & Philadelphia-specific disclosures are included automatically.
            Consult a licensed attorney for edge cases.
          </p>
          <button onClick={download} disabled={busy}
            data-testid="lb-download"
            className="h-12 px-8 border-2 border-[#0B1F3A] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0B1F3A] hover:text-white font-black uppercase tracking-widest shadow-[3px_3px_0px_#0B1F3A] disabled:opacity-50">
            {busy ? "Building PDF…" : "📄 Download Lease PDF"}
          </button>
        </div>

        {/* Post-download upsell */}
        {downloaded && (
          <div className="mt-4 mb-8 p-5 bg-white border-2 border-cyan-500 rounded-xl shadow-[6px_6px_0px_#00C4CC]"
            data-testid="lb-upsell">
            <div className="font-display font-black text-xl text-[#0B1F3A]">🎉 Your lease is downloaded.</div>
            <p className="text-sm text-slate-600 mt-2">
              Want us to send it for e-signature, collect rent automatically, and route repair
              requests to the right vendor? Field Radar's Landlord Autopilot handles it in 3 clicks.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Link to="/landlord/signup"
                data-testid="lb-signup"
                className="h-10 px-4 inline-flex items-center border-2 border-[#0B1F3A] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0B1F3A] hover:text-white font-black uppercase tracking-widest text-xs">
                Start Free Trial →
              </Link>
              <Link to="/landlord/faq"
                className="h-10 px-4 inline-flex items-center border-2 border-slate-300 hover:border-[#0B1F3A] text-slate-700 font-black uppercase tracking-widest text-xs">
                Read New-Landlord FAQ
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const inputCls = "w-full h-10 px-3 border-2 border-slate-200 focus:border-cyan-500 outline-none rounded font-medium text-sm";
const txtCls = "w-full px-3 py-2 border-2 border-slate-200 focus:border-cyan-500 outline-none rounded text-sm";
function Section({ title, children }) {
  return (
    <section className="bg-white border-2 border-slate-200 rounded-xl p-4 sm:p-5 mb-4">
      <h2 className="font-display font-black text-base text-[#0B1F3A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function F({ lbl, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">{lbl}</div>
      {children}
    </label>
  );
}
function I({ v, on, type = "text", tid }) {
  return <input type={type} value={v} onChange={e => on(e.target.value)} data-testid={tid} className={inputCls}/>;
}
function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}
